import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Leaf, Utensils, Ban, Clock, Link2, Sparkles, Heart, TriangleAlert, ChevronRight } from "lucide-react";
import { ORGANS, getOrgan } from "@/data/organs";
import { elementColor } from "@/data/theme";
import { ElementBadge, SafetyNote, SourceTag } from "@/components/common";

const Card = ({ icon: Icon, title, color, children, testid, id, className = "" }) => (
  <section id={id} data-testid={testid} className={`rounded-3xl border border-border bg-card p-6 sm:p-7 ${className}`}>
    <div className="mb-4 flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: color.soft, color: color.primary }}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <h2 className="text-lg font-serif font-medium text-foreground">{title}</h2>
    </div>
    {children}
  </section>
);

const QUICK_NAV_ITEMS = [
  { id: "visao-geral", label: "Visão geral" },
  { id: "funcoes-habitos", label: "Funções & Hábitos" },
  { id: "alimentacao-chas", label: "Alimentação & Chás" },
  { id: "relacoes", label: "Relações" },
  { id: "cuidados", label: "Cuidados" },
];

export default function OrganDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const o = getOrgan(id);

  if (!o) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Órgão não encontrado no guia.</p>
        <Link to="/orgaos" className="text-gold font-medium">Voltar aos órgãos</Link>
      </div>
    );
  }

  const c = elementColor(o.elemento);
  const idx = ORGANS.findIndex((x) => x.id === id);
  const prev = ORGANS[(idx - 1 + ORGANS.length) % ORGANS.length];
  const next = ORGANS[(idx + 1) % ORGANS.length];

  const scrollToSection = (e, targetId) => {
    e.preventDefault();
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="space-y-8">
      <button
        data-testid="back-to-organs"
        onClick={() => navigate("/orgaos")}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Todos os órgãos
      </button>

      {/* HERO */}
      <div
        id="visao-geral"
        className="scroll-mt-20 sm:scroll-mt-24 overflow-hidden rounded-3xl border border-gold/25 p-8 sm:p-10"
        style={{ background: `linear-gradient(160deg, ${c.soft}, hsl(var(--card)))` }}
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 lg:col-span-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <ElementBadge elementKey={o.elemento} label={`Elemento ${o.elementoLabel}`} />
              <SourceTag pages={o.sourcePages} />
            </div>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" style={{ color: c.text }}>
              {o.name}
            </h1>
            <p className="mt-2 text-lg italic" style={{ color: c.primary }}>{o.subtitle}</p>
            <p className="mt-5 text-base leading-relaxed" style={{ color: c.text }}>{o.intro}</p>
          </div>
          <div className="md:col-span-5 lg:col-span-4 flex justify-center items-center">
            <img
              src={`/images/organs/${o.id}.webp`}
              alt={o.name}
              className="w-48 sm:w-56 md:w-full max-w-[280px] h-auto object-contain select-none drop-shadow-sm"
              loading="eager"
            />
          </div>
        </div>

        <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { k: "Elemento", v: o.elementoLabel },
            { k: "Sabor", v: o.sabor },
            { k: "Emoção associada", v: o.emocao },
            { k: "Melhor horário", v: o.relogio.periodo },
          ].map((f) => (
            <div key={f.k} className="rounded-2xl bg-background/60 p-4">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{f.k}</p>
              <p className="mt-1 font-medium text-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* NAVEGAÇÃO RÁPIDA */}
      <nav
        aria-label="Navegação rápida pela ficha do órgão"
        data-testid="organ-quick-nav"
        className="rounded-2xl border border-border/80 bg-card/60 p-1.5 shadow-sm backdrop-blur-sm"
      >
        <ul className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden py-0.5 px-0.5" role="list">
          {QUICK_NAV_ITEMS.map((item) => (
            <li key={item.id} className="shrink-0">
              <a
                href={`#${item.id}`}
                data-testid={`quick-nav-${item.id}`}
                onClick={(e) => scrollToSection(e, item.id)}
                className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-xl px-3.5 py-2 text-xs sm:text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Função + Funções principais */}
      <div id="funcoes-habitos" className="scroll-mt-20 sm:scroll-mt-24 grid gap-6 lg:grid-cols-2">
        <Card icon={Heart} title="Função na MTC" color={c} testid="organ-funcao">
          <p className="text-sm leading-relaxed text-foreground">{o.funcao}</p>
        </Card>
        <Card icon={Sparkles} title="Funções principais" color={c} testid="organ-funcoes">
          <ul className="space-y-2">
            {o.funcoesPrincipais.map((f, i) => (
              <li key={i} className="flex gap-2 text-sm text-foreground">
                <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" style={{ color: c.primary }} /> {f}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Hábitos */}
      <Card icon={Leaf} title="Hábitos que apoiam o equilíbrio" color={c} testid="organ-habitos">
        <div className="grid gap-2 sm:grid-cols-2">
          {o.habitos.map((h, i) => (
            <div key={i} className="flex gap-2 rounded-xl bg-secondary/60 px-4 py-3 text-sm text-foreground">
              <span className="text-gold">•</span> {h}
            </div>
          ))}
        </div>
      </Card>

      {/* Chás */}
      <Card
        id="alimentacao-chas"
        className="scroll-mt-20 sm:scroll-mt-24"
        icon={Leaf}
        title="Chás citados no guia"
        color={c}
        testid="organ-chas"
      >
        <div className="grid gap-3 sm:grid-cols-3">
          {o.chas.map((t, i) => (
            <div key={i} className="rounded-2xl border border-border p-4">
              <p className="font-medium text-foreground">{t.nome}</p>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t.beneficio}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Referência educativa. Não indica dose ou frequência. {o.melhorHorario ? `${o.melhorHorario} ` : ""}{o.dicaCha || ""}
        </p>
      </Card>

      {/* Alimentos + Evitar */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card icon={Utensils} title={o.alimentosTitulo || "Alimentos que fortalecem"} color={c} testid="organ-alimentos">
          <div className="flex flex-wrap gap-2">
            {o.alimentos.map((a, i) => (
              <span key={i} className="rounded-full px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: c.soft, color: c.text }}>{a}</span>
            ))}
          </div>
          {o.alimentosNota && (
            <p className="mt-4 text-xs text-muted-foreground leading-relaxed italic">
              {o.alimentosNota}
            </p>
          )}
        </Card>
        <Card icon={Ban} title="Evitar ou reduzir" color={c} testid="organ-evitar">
          <ul className="grid gap-2 sm:grid-cols-2">
            {o.evitar.map((e, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground"><Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive/70" /> {e}</li>
            ))}
          </ul>
          {o.evitarAtencao && (
            <p className="mt-4 text-xs text-muted-foreground leading-relaxed border-t border-border/50 pt-3">
              {o.evitarAtencao}
            </p>
          )}
        </Card>
      </div>

      {/* Reflexão */}
      <div className="rounded-3xl border border-gold/25 bg-gold/5 p-8 text-center">
        <p className="font-serif text-xl sm:text-2xl italic text-foreground leading-relaxed">“{o.reflexao}”</p>
      </div>

      {/* Relógio + Relações */}
      <div id="relacoes" className="scroll-mt-20 sm:scroll-mt-24 grid gap-6 lg:grid-cols-2">
        <Card icon={Clock} title="Relógio dos órgãos" color={c} testid="organ-relogio">
          <p className="font-serif text-2xl font-semibold" style={{ color: c.primary }}>{o.relogio.periodo}</p>
          <p className="mt-2 text-sm leading-relaxed text-foreground">{o.relogio.descricao}</p>
          <Link to="/relogio" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:gap-2.5 transition-all">
            Ver o relógio completo <ArrowRight className="h-4 w-4" />
          </Link>
        </Card>
        <Card icon={Link2} title="Relações importantes" color={c} testid="organ-relacoes">
          <p className="text-sm text-muted-foreground">Órgão parceiro</p>
          <Link
            to={`/orgaos/${o.relacoes.parceiro}`}
            data-testid="organ-partner-link"
            className="mt-1 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:scale-105"
            style={{ backgroundColor: c.soft, color: c.text }}
          >
            {o.relacoes.parceiroLabel} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <p className="mt-4 text-sm text-foreground"><span className="text-muted-foreground">Pode afetar:</span> {o.relacoes.podeAfetar}</p>
        </Card>
      </div>

      {/* Curiosidades */}
      <Card icon={Sparkles} title="Curiosidades" color={c} testid="organ-curiosidades">
        <ul className="space-y-3">
          {o.curiosidades.map((cu, i) => (
            <li key={i} className="text-sm leading-relaxed text-foreground">{cu}</li>
          ))}
        </ul>
      </Card>

      {/* Quando procurar */}
      <section
        id="cuidados"
        data-testid="organ-quando-procurar"
        className="scroll-mt-20 sm:scroll-mt-24 rounded-3xl border border-destructive/30 bg-destructive/5 p-6 sm:p-7"
      >
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
            <TriangleAlert className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <h2 className="text-lg font-serif font-medium text-foreground">Quando procurar avaliação profissional</h2>
        </div>
        <ul className="grid gap-2 sm:grid-cols-2">
          {o.quandoProcurar.map((q, i) => (
            <li key={i} className="flex gap-2 text-sm text-foreground"><span className="text-destructive">•</span> {q}</li>
          ))}
        </ul>
        <p className="mt-4 text-sm font-medium text-destructive">Não se automedique. Busque orientação de um profissional de saúde.</p>
      </section>

      {/* Registrar observação */}
      <div className="rounded-2xl border border-border/60 bg-card/40 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm sm:text-base font-medium text-foreground">Registrar uma observação</p>
          <p className="text-xs text-muted-foreground mt-0.5">Anote o que você percebeu no seu dia.</p>
        </div>
        <Link
          to="/registros"
          data-testid="organ-register-observation"
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border/80 bg-secondary/50 px-4 py-2 text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          Registrar uma observação <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Prev / Next */}
      <div className="flex items-center justify-between gap-4 border-t border-border pt-6">
        <Link to={`/orgaos/${prev.id}`} data-testid="organ-prev" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {prev.name}
        </Link>
        <Link to={`/orgaos/${next.id}`} data-testid="organ-next" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          {next.name} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
